import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../../../shared/ui/Seo";
import AccountSidebar from "../../account/components/AccountSidebar";
import ComplaintSubmitModal from "./ComplaintSubmitModal";
import { getOrderbyId, trackOrder } from "../api";
import { openInvoicePdf } from "../utils/invoice";
import { AddressCard, SummaryRow, OrderItemsCard, hasValue } from "./OrderInfo";
import { CURRENCY } from "../../../shared/utils/constants";
import "../styles/my-orders.css";
import "../styles/order-details.css";

const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let ignore = false;
    // A refresh after a complaint keeps the page on screen instead of the loading state.
    if (reloadKey === 0) setLoading(true);
    setHasError(false);
    getOrderbyId(id)
      .then((response) => {
        if (!ignore) setOrder(response);
      })
      .catch((error) => {
        console.error("Error fetching order:", error);
        if (!ignore) setHasError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [id, reloadKey]);

  const handleInvoice = async () => {
    setInvoiceLoading(true);
    try {
      openInvoicePdf(await trackOrder(order.orderId));
    } catch (error) {
      console.error("Error fetching order details or generating invoice:", error);
    } finally {
      setInvoiceLoading(false);
    }
  };

  let content;
  if (loading) {
    content = (
      <div className="od-grid" aria-hidden="true">
        <div className="od-card">
          <span className="skeleton-line w-120" />
          <div className="order-item"><span className="order-item__thumb skeleton-block" /><span className="skeleton-line w-200" /></div>
          <div className="order-item"><span className="order-item__thumb skeleton-block" /><span className="skeleton-line w-200" /></div>
        </div>
        <div className="od-card"><span className="skeleton-line w-200" /></div>
      </div>
    );
  } else if (hasError || !order) {
    content = (
      <div className="ui-empty">
        <i className="ph ph-receipt-x ui-empty__icon" />
        <h2 className="ui-empty__title">We couldn't find this order</h2>
        <p className="ui-empty__text">It may have been removed, or the link is incorrect.</p>
        <Link to="/my-orders" className="ui-btn ui-btn--primary ui-btn--sm">Back to My Orders</Link>
      </div>
    );
  } else {
    const items = order.orderItems || [];
    const currency = order.currency || CURRENCY;
    content = (
      <>
        <header className="od-head">
          <div className="od-head__info">
            <div className="od-head__title-row">
              <h1 className="od-head__title">Order #{order.orderId}</h1>
              {hasValue(order.status) && <span className="order-status">{order.status}</span>}
            </div>
            {hasValue(order.createdAt) && (
              <p className="od-head__meta">
                Placed on {new Date(order.createdAt).toLocaleString()}
              </p>
            )}
          </div>
          <div className="od-head__actions">
            <button
              type="button"
              className="ui-btn ui-btn--ghost ui-btn--sm"
              onClick={handleInvoice}
              disabled={invoiceLoading}
            >
              <i className={`ph ${invoiceLoading ? "ph-spinner ui-btn__spin" : "ph-file-text"}`} />
              {invoiceLoading ? "Preparing…" : "Invoice"}
            </button>
            {!order.complaint && (
              <button
                type="button"
                className="ui-btn ui-btn--ghost ui-btn--sm"
                onClick={() => setComplaintOpen(true)}
              >
                <i className="ph ph-chat-circle-text" />
                Report a problem
              </button>
            )}
          </div>
        </header>

        <div className="od-grid">
          <div className="od-col">
            <OrderItemsCard
              currency={currency}
              items={items.map((item, index) => ({
                key: item.productId || index,
                name: item.productName,
                image: item.productImages?.[0],
                quantity: item.quantity,
                price: item.price,
              }))}
            />

            {order.complaint && (
              <section className="od-card">
                <div className="od-card__title-row">
                  <h2 className="od-card__title">Your complaint</h2>
                  {hasValue(order.complaint.status) && (
                    <span className="order-status">{order.complaint.status}</span>
                  )}
                </div>
                <p className="od-text">{order.complaint.content}</p>
              </section>
            )}

            {hasValue(order.customerNote) && (
              <section className="od-card">
                <h2 className="od-card__title">Order note</h2>
                <p className="od-text">{order.customerNote}</p>
              </section>
            )}
          </div>

          <div className="od-col">
            <section className="od-card">
              <h2 className="od-card__title">Payment summary</h2>
              <div className="od-summary">
                {hasValue(order.paymentMethodTitle) && (
                  <div className="od-summary__row">
                    <span>Payment method</span>
                    <span className="od-summary__method">{order.paymentMethodTitle}</span>
                  </div>
                )}
                <SummaryRow label="Subtotal" value={order.totalAmount} currency={currency} />
                <SummaryRow label="Delivery fee" value={order.deliveryFee} currency={currency} />
                <SummaryRow label="Discount" value={order.discount} currency={currency} />
                <SummaryRow label="Tax" value={order.tax} currency={currency} />
                <SummaryRow label="Total" value={order.netAmount} currency={currency} strong />
              </div>
            </section>

            <AddressCard title="Shipping address" address={order.shippingAddress} />
            <AddressCard title="Billing address" address={order.billingAddress} />
          </div>
        </div>

        <ComplaintSubmitModal
          orderNumber={order.orderId}
          isOpen={complaintOpen}
          onClose={() => {
            setComplaintOpen(false);
            setReloadKey((key) => key + 1);
          }}
        />
      </>
    );
  }

  return (
    <section className="account-page py-20">
      <Seo title={order ? `Order ${order.orderId}` : "Order Details"} noindex />
      <div className="account-layout">
        <AccountSidebar />
        <div className="account-main">
          <Link to="/my-orders" className="od-back">
            <i className="ph ph-arrow-left" aria-hidden="true" /> My Orders
          </Link>
          {content}
        </div>
      </div>
    </section>
  );
};

export default OrderDetails;
